import React from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import TraderQuizShell from './TraderQuizShell';
import {
  ARCHETYPES,
  TOOLS_BY_ARCHETYPE,
  TOOL_BLURBS,
  getQuizArchetypeImageSrc,
  isValidArchetypeKey,
  splitArchetypeBodyParagraphs,
} from '../data/quizData';
import '../components/Quiz/Quiz.css';

const TraderQuizProfileDetail = () => {
  const { archetypeKey } = useParams();
  if (!isValidArchetypeKey(archetypeKey)) {
    return <Navigate to="/quiz/profiles" replace />;
  }

  const primary = ARCHETYPES[archetypeKey];
  const archetypeImageSrc = getQuizArchetypeImageSrc(archetypeKey);
  const bodyParagraphs = splitArchetypeBodyParagraphs(primary.body);
  const tools = (TOOLS_BY_ARCHETYPE[archetypeKey] || [])
    .map((id) => TOOL_BLURBS[id])
    .filter(Boolean);

  return (
    <TraderQuizShell>
      <div
        className="quiz-shell quiz-shell-page-inset mx-auto px-3 px-md-4 pb-4 text-start"
        style={{ maxWidth: 720 }}
      >
        <div className="mb-3">
          <Link
            to="/quiz/profiles"
            className="btn btn-link btn-sm quiz-inline-nav-btn p-0 d-inline-flex align-items-center gap-1"
          >
            ← Archetypes overview
          </Link>
        </div>

        <header className="quiz-results-hero mb-4">
          {archetypeImageSrc ? (
            <img
              src={archetypeImageSrc}
              alt=""
              className="quiz-results-archetype-illustration"
              width={176}
              height={176}
              loading="lazy"
              decoding="async"
            />
          ) : null}
          <div className="quiz-results-hero-text">
            <h1 className="quiz-results-archetype-title h2 mb-2">{primary.title}</h1>
            <p className="text-muted fst-italic mb-0 quiz-results-tagline">{primary.tagline}</p>
          </div>
        </header>

        <section className="quiz-results-panel quiz-results-panel-profile mb-4">
          {primary.intro ? <p className="mb-3 quiz-archetype-prose">{primary.intro}</p> : null}
          {bodyParagraphs.map((para, idx) => (
            <p key={idx} className={`quiz-archetype-prose mb-0 ${primary.intro || idx > 0 ? 'mt-3' : ''}`}>
              {para}
            </p>
          ))}

          {primary.recognitions?.length ? (
            <div className="quiz-results-archetype-subsection mt-4">
              <h2 className="quiz-section-heading quiz-section-heading--accent h6 mb-2">Sound familiar?</h2>
              <ul className="quiz-archetype-recognitions small mb-0">
                {primary.recognitions.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
            </div>
          ) : null}

          {primary.bodyClosing ? (
            <div className="quiz-results-archetype-subsection mt-4">
              <h2 className="quiz-section-heading quiz-section-heading--callout h6 mb-2">
                How PrecisionTrader helps
              </h2>
              <p className="mb-0 small quiz-archetype-prose">{primary.bodyClosing}</p>
            </div>
          ) : null}

          {primary.cta ? (
            <p className="mb-0 mt-3 small quiz-archetype-prose fw-semibold text-light">{primary.cta}</p>
          ) : null}
        </section>

        {tools.length > 0 ? (
          <section className="quiz-results-panel quiz-results-panel-tools mb-4">
            <h2 className="quiz-section-heading quiz-section-heading--tools h6 mb-3">Tools that often help this profile</h2>
            <ul className="list-unstyled mb-0 small">
              {tools.map((t) => (
                <li key={t.name} className="mb-3 pb-3 border-bottom border-secondary border-opacity-25">
                  <div className="text-light fw-semibold">{t.name}</div>
                  <div className="text-muted small">{t.description}</div>
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        <div className="text-center">
          <Link to="/quiz" className="btn btn-primary btn-lg px-5">
            Take the FREE Quiz
          </Link>
        </div>
      </div>
    </TraderQuizShell>
  );
};

export default TraderQuizProfileDetail;
